import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPost, authPut } from "../../lib";

/**
 * Coupons — a port of the legacy coupon.php screen.
 *
 * A coupon is either a fixed discount ($ off) or a fixed total price, pinned
 * to one or more specific watches. The model number typed here is resolved to
 * an item id through the same lookup the New Order screen uses, because that
 * is what the column actually stores.
 */

const KINDS = [
  { key: "item", label: "Item-specific" },
  { key: "general", label: "General" },
  { key: "all", label: "All" },
];

const STATE_COLOR = {
  Active: "27,94,32",
  Inactive: "97,97,97",
  Used: "224,168,0",
};

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1360, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2.5), marginBottom: theme.spacing(2) },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 500, flex: "none" },
  spacer: { flex: 1 },
  search: {
    width: 280,
    "& .MuiOutlinedInput-root": { height: 31 },
    "& .MuiOutlinedInput-input": { padding: "0 4px", fontSize: 13 },
    "& .MuiInputAdornment-root": { marginRight: 2 },
  },
  searchIcon: { fontSize: 17, color: theme.palette.text.secondary },
  count: { color: theme.palette.text.secondary, fontSize: 13 },
  form: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(1.5),
    alignItems: "start",
  },
  formTitle: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".02em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  flags: {
    gridColumn: "span 4",
    paddingTop: 6,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  chips: {
    gridColumn: "span 12",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    minHeight: 26,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "2px 6px 2px 8px",
    borderRadius: 3,
    fontSize: 12,
    background: "rgba(49,81,121,0.10)",
    color: "#315179",
  },
  chipPre: { background: "rgba(74,20,140,0.10)", color: "#4a148c" },
  chipX: {
    cursor: "pointer",
    fontWeight: 700,
    opacity: 0.55,
    "&:hover": { opacity: 1 },
  },
  row: { height: 36 },
  cell: { fontSize: 13, padding: "4px 10px" },
  headCell: { fontSize: 12, fontWeight: 600, padding: "6px 10px" },
  num: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
  muted: { color: theme.palette.text.secondary },
  strong: { color: "rgba(0,0,0,0.87)" },
  state: {
    display: "inline-block",
    padding: "1px 7px",
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".03em",
  },
  itemsBtn: {
    fontSize: 12,
    padding: 0,
    minWidth: 0,
    textTransform: "none",
    color: theme.palette.primary.main,
  },
  itemList: { fontSize: 12.5, lineHeight: 1.7 },
  editBtn: { padding: 4, color: "rgba(0,0,0,0.4)", "&:hover": { color: "#315179" } },
  /* Activate/Deactivate and the pencil, evenly spaced rather than touching. */
  actions: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  ok: { color: "#1b5e20", fontSize: 13, marginTop: theme.spacing(1) },
  loading: { padding: theme.spacing(4), textAlign: "center" },
}));

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

const onlyDate = (s) => (s ? String(s).slice(0, 10) : "");

const EMPTY = {
  coupon_code: "",
  coupon_name: "",
  start: "",
  end: "",
  value: "",
  order_total: "",
  one_time: false,
  active: true,
};

/**
 * The legacy cog button: makeid(5) in coupon.js, over an alphabet with no
 * letter O — five characters, so codes stay short enough to read down a phone.
 */
function generateCode() {
  const alphabet = "ABCDEFGHIJKLMNPQRSTUVWXYZ0123456789";
  let out = "";
  for (let i = 0; i < 5; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

export function CouponsPage() {
  const classes = useStyles();

  const [kind, setKind] = useState("item");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [model, setModel] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [openItems, setOpenItems] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams({ kind });
    if (query) q.set("search", query);
    const { data, error: err } = await authGet(`/coupons?${q.toString()}`);
    setLoading(false);

    if (err) {
      setError(err.message || "Could not load coupons.");
      return;
    }
    setRows(data?.rows ?? []);
    setTotal(data?.total ?? 0);
  }, [kind, query]);

  useEffect(() => {
    load();
  }, [load]);

  const addItem = async () => {
    const wanted = model.trim();
    if (!wanted) return;
    setError("");

    // The same endpoint the New Order screen uses: it takes a model number, or
    // P + stock number for a pre-owned piece.
    const { data, error: err } = await authGet(
      `/orders/item-lookup?model_no=${encodeURIComponent(wanted)}`,
    );
    if (err) {
      setError(err.message || "Could not look that model up.");
      return;
    }
    if (!data?.found) {
      setError(`No item has the model number "${wanted}".`);
      return;
    }

    // Stored as the ITEM id — P-prefixed for pre-owned, which is a different
    // meaning of P from the one typed above.
    const token = data.is_preowned ? `P${data.item_id}` : String(data.item_id);
    if (items.some((i) => i.token === token)) {
      setModel("");
      return;
    }
    setItems((list) => [
      ...list,
      { token, label: data.label || data.model_no, preowned: !!data.is_preowned },
    ]);
    setModel("");
  };

  const reset = () => {
    setForm(EMPTY);
    setItems([]);
    setEditingId(null);
    setModel("");
    setError("");
  };

  const edit = async (r) => {
    setError("");
    setNotice("");
    setEditingId(r.coupon_id);
    setForm({
      coupon_code: r.coupon_code ?? "",
      coupon_name: r.coupon_name ?? "",
      start: onlyDate(r.start),
      end: onlyDate(r.end),
      value: r.value ?? "",
      order_total: r.order_total ?? "",
      one_time: !!Number(r.one_time),
      active: !!Number(r.active),
    });

    const { data } = await authGet(`/coupons/${r.coupon_id}/items`);
    setItems(
      (data?.items ?? []).map((i) => ({
        token: i.token,
        label: i.label || (i.missing ? `${i.token} (not found)` : i.token),
        preowned: !!i.preowned,
      })),
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    setBusy(true);
    setError("");
    setNotice("");

    const body = {
      ...form,
      value: form.value === "" ? null : form.value,
      order_total: form.order_total === "" ? null : form.order_total,
      items: items.map((i) => i.token),
    };

    const { error: err } = editingId
      ? await authPut(`/coupons/${editingId}`, body)
      : await authPost("/coupons", body);
    setBusy(false);

    if (err) {
      setError(err.message || "Could not save that coupon.");
      return;
    }
    setNotice(editingId ? "Coupon updated." : "Coupon created.");
    reset();
    load();
  };

  const toggle = (r) => async () => {
    const { error: err } = await authPut(`/coupons/${r.coupon_id}/status`, {});
    if (err) {
      setError(err.message || "Could not change that coupon.");
      return;
    }
    load();
  };

  const showItems = (r) => async () => {
    if (openItems[r.coupon_id]) {
      setOpenItems((o) => ({ ...o, [r.coupon_id]: null }));
      return;
    }
    setOpenItems((o) => ({ ...o, [r.coupon_id]: "loading" }));
    const { data } = await authGet(`/coupons/${r.coupon_id}/items`);
    setOpenItems((o) => ({ ...o, [r.coupon_id]: data?.items ?? [] }));
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    setQuery(search.trim());
  };

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <div className={classes.formTitle}>
          {editingId ? `Edit Coupon #${editingId}` : "New Coupon"}
        </div>

        <div className={classes.form}>
          <TextField
            style={{ gridColumn: "span 3" }}
            label="Code"
            value={form.coupon_code}
            onChange={(e) => setForm((f) => ({ ...f, coupon_code: e.target.value }))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    className={classes.itemsBtn}
                    onClick={() => setForm((f) => ({ ...f, coupon_code: generateCode() }))}
                  >
                    Generate
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            style={{ gridColumn: "span 3" }}
            label="Name"
            value={form.coupon_name}
            onChange={(e) => setForm((f) => ({ ...f, coupon_name: e.target.value }))}
          />
          <TextField
            style={{ gridColumn: "span 3" }}
            label="Starts"
            type="date"
            value={form.start}
            onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
          />
          <TextField
            style={{ gridColumn: "span 3" }}
            label="Ends"
            type="date"
            helperText="Stops at the start of this day"
            value={form.end}
            onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
          />

          <TextField
            style={{ gridColumn: "span 2" }}
            label="Discount ($ off)"
            type="number"
            value={form.value}
            onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
          />
          <TextField
            style={{ gridColumn: "span 2" }}
            label="Total Price"
            type="number"
            value={form.order_total}
            onChange={(e) => setForm((f) => ({ ...f, order_total: e.target.value }))}
          />

          <TextField
            style={{ gridColumn: "span 4" }}
            label="Model Number"
            placeholder="Model number, or P + stock number"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addItem();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" className={classes.itemsBtn} onClick={addItem}>
                    Add
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          <div className={classes.flags}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={form.one_time}
                  onChange={(e) => setForm((f) => ({ ...f, one_time: e.target.checked }))}
                />
              }
              label="One time"
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </div>

          <div className={classes.chips}>
            {!items.length ? (
              <span className={classes.muted} style={{ fontSize: 12.5 }}>
                No watches added yet — a coupon needs at least one.
              </span>
            ) : (
              items.map((i) => (
                <span
                  key={i.token}
                  className={`${classes.chip} ${i.preowned ? classes.chipPre : ""}`}
                  title={i.token}
                >
                  {i.label}
                  <span
                    className={classes.chipX}
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      if (!window.confirm("Are you sure you want to remove this item?")) return;
                      setItems((l) => l.filter((x) => x.token !== i.token));
                    }}
                  >
                    ×
                  </span>
                </span>
              ))
            )}
          </div>
        </div>

        {error && <div className={classes.error}>{error}</div>}
        {notice && <div className={classes.ok}>{notice}</div>}

        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy}
            onClick={save}
          >
            {busy ? "Saving…" : editingId ? "Save changes" : "Save Coupon"}
          </Button>
          {editingId && (
            <Button size="small" onClick={reset} disabled={busy}>
              Cancel
            </Button>
          )}
        </div>
      </Paper>

      <Paper className={classes.panel}>
        <div className={classes.head}>
          <Typography className={classes.title}>Coupons</Typography>

          <ButtonGroup size="small">
            {KINDS.map((k) => (
              <Button
                key={k.key}
                onClick={() => setKind(k.key)}
                variant={kind === k.key ? "contained" : "outlined"}
                color={kind === k.key ? "primary" : "default"}
              >
                {k.label}
              </Button>
            ))}
          </ButtonGroup>

          <form onSubmit={submitSearch}>
            <TextField
              className={classes.search}
              placeholder="Code or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={submitSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </form>

          <div className={classes.spacer} />
          <span className={classes.count}>
            {loading ? "Loading…" : `${total.toLocaleString()} shown`}
          </span>
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell} style={{ width: 130 }}>Code</TableCell>
              <TableCell className={classes.headCell}>Name</TableCell>
              <TableCell className={classes.headCell} style={{ width: 100 }}>Starts</TableCell>
              <TableCell className={classes.headCell} style={{ width: 100 }}>Ends</TableCell>
              <TableCell className={classes.headCell} style={{ width: 150 }}>Items</TableCell>
              <TableCell className={`${classes.headCell} ${classes.num}`} style={{ width: 110 }}>
                Value
              </TableCell>
              <TableCell className={classes.headCell} style={{ width: 90 }}>Status</TableCell>
              <TableCell className={classes.headCell} style={{ width: 120 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const rgb = STATE_COLOR[r.state] ?? STATE_COLOR.Inactive;
              const shown = openItems[r.coupon_id];
              return (
                <React.Fragment key={r.coupon_id}>
                  <TableRow className={classes.row}>
                    <TableCell className={`${classes.cell} ${classes.strong}`}>
                      {r.coupon_code}
                    </TableCell>
                    <TableCell className={classes.cell}>{r.coupon_name}</TableCell>
                    <TableCell className={classes.cell}>{onlyDate(r.start)}</TableCell>
                    <TableCell className={classes.cell}>{onlyDate(r.end)}</TableCell>
                    <TableCell className={classes.cell}>
                      {r.items.length ? (
                        <Button className={classes.itemsBtn} onClick={showItems(r)}>
                          {shown ? "Hide" : `${r.items.length} item${r.items.length > 1 ? "s" : ""}`}
                        </Button>
                      ) : (
                        <span className={classes.muted}>Any item</span>
                      )}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {Number(r.value) > 0 ? `${money(r.value)} off` : money(r.order_total)}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <span
                        className={classes.state}
                        style={{ background: `rgba(${rgb},0.16)`, color: `rgb(${rgb})` }}
                      >
                        {r.state}
                      </span>
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.actions}`}>
                      <Button className={classes.itemsBtn} onClick={toggle(r)}>
                        {Number(r.active) ? "Deactivate" : "Activate"}
                      </Button>
                      <IconButton
                        size="small"
                        className={classes.editBtn}
                        title="Edit this coupon"
                        onClick={() => edit(r)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {shown && (
                    <TableRow>
                      <TableCell className={classes.cell} colSpan={8}>
                        {shown === "loading" ? (
                          <span className={classes.muted}>Loading…</span>
                        ) : (
                          <div className={classes.itemList}>
                            {shown.map((i) => (
                              <div key={i.token}>
                                {i.preowned ? "Pre-owned · " : ""}
                                {i.label ?? (
                                  <span className={classes.muted}>
                                    {i.token} — no longer in the catalog
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={22} />
          </div>
        )}
        {!loading && !rows.length && (
          <div className={classes.loading}>
            <span className={classes.muted}>No coupons match.</span>
          </div>
        )}
      </Paper>
    </Box>
  );
}

export default CouponsPage;
